import { isAmazonHost } from './isAmazonHost';

/**
 * Utility function to extract product identifiers from the browser's URL
 *
 * Supports multiple URL patterns:
 * - Amazon URLs: Extracts ASIN (10-character alphanumeric ID)
 *   - /dp/ASIN (e.g., amazon.com/dp/B08N5WRWNW)
 *   - /gp/product/ASIN (e.g., amazon.com/gp/product/B08N5WRWNW)
 *   - Query parameter ?asin=ASIN
 *
 * - Ring URLs: Extracts product name
 *   - /products/{product-name} (e.g., ring.com/products/battery-doorbell)
 *
 * @returns {string | null} product ID or null
 */
const ASIN_PATTERN = '[A-Z0-9]{10}';

const extractAsin = (urlObj: URL): string | null => {
  const dpMatch = urlObj.pathname.match(
    new RegExp(`/dp/(${ASIN_PATTERN})`, 'i')
  );
  if (dpMatch && dpMatch[1]) {
    return dpMatch[1];
  }

  const gpMatch = urlObj.pathname.match(
    new RegExp(`/gp/product/(${ASIN_PATTERN})`, 'i')
  );
  if (gpMatch && gpMatch[1]) {
    return gpMatch[1];
  }

  const asinParam = urlObj.searchParams.get('asin');
  if (asinParam && new RegExp(`^${ASIN_PATTERN}$`, 'i').test(asinParam)) {
    return asinParam;
  }

  const genericMatch = urlObj.pathname.match(
    new RegExp(`/(${ASIN_PATTERN})(?:/|$)`, 'i')
  );
  if (genericMatch && genericMatch[1]) {
    return genericMatch[1];
  }

  return null;
};

const extractRingProduct = (urlObj: URL): string | null => {
  const ringMatch = urlObj.pathname.match(/\/products\/([^/]+)/i);
  if (ringMatch && ringMatch[1]) {
    return ringMatch[1];
  }

  return null;
};

export const getProductIdFromUrl = (): string | null => {
  try {
    const urlObj = new URL(window.location.href);
    return isAmazonHost() ? extractAsin(urlObj) : extractRingProduct(urlObj);
  } catch (error) {
    return null;
  }
};
